import { useEffect, useRef } from 'react';
import {
  DIRECT_TERMINAL_GUIDANCE,
  NORMAL_WORKFLOW,
  WORKFLOW_CHAIN,
  WORKFLOW_DEFINITIONS,
  WORKFLOW_TOPICS,
} from '../lib/workflow-guide';
import '../workflow-guide.css';

export function WorkflowGuide({ onClose }: { onClose: () => void }) {
  const closeButton = useRef<HTMLButtonElement | null>(null);

  useEffect(() => {
    const previousFocus = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    closeButton.current?.focus();

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      event.preventDefault();
      onClose();
    };
    window.addEventListener('keydown', onKeyDown);

    return () => {
      window.removeEventListener('keydown', onKeyDown);
      if (previousFocus?.isConnected) previousFocus.focus();
    };
  }, [onClose]);

  return (
    <aside className="drawer workflow-guide" role="dialog" aria-modal="false" aria-labelledby="workflow-guide-title">
      <header className="row spread drawer-head">
        <div>
          <h2 id="workflow-guide-title">How Brunel Works</h2>
          <p className="workflow-guide-kicker">The normal path from your goal to finished work.</p>
        </div>
        <button ref={closeButton} type="button" className="link" aria-label="Close How Brunel Works" onClick={onClose}>
          Close
        </button>
      </header>

      <div className="drawer-body workflow-guide-body">
        <section aria-labelledby="workflow-chain-title">
          <h3 id="workflow-chain-title">The normal chain</h3>
          <ol className="workflow-chain" aria-label="Normal Brunel chain of command">
            {WORKFLOW_CHAIN.map((step) => <li key={step}>{step}</li>)}
          </ol>
          <p className="muted">New work and normal Worker conversation stay in Main Command. A Worker Terminal is only the direct low-level intervention path for that one Worker.</p>
        </section>

        <section aria-labelledby="workflow-parts-title">
          <h3 id="workflow-parts-title">What each part does</h3>
          <div className="workflow-topic-list">
            {WORKFLOW_TOPICS.map((topic) => (
              <article key={topic.id} className="workflow-topic">
                <h4>{topic.title}</h4>
                <p>{topic.summary}</p>
              </article>
            ))}
          </div>
        </section>

        <section aria-labelledby="workflow-terms-title">
          <h3 id="workflow-terms-title">Computer, node, Worker, terminal, and task</h3>
          <dl className="workflow-definitions">
            {WORKFLOW_DEFINITIONS.map((item) => (
              <div key={item.term}>
                <dt>{item.term}</dt>
                <dd>{item.meaning}</dd>
              </div>
            ))}
          </dl>
        </section>

        <section aria-labelledby="workflow-normal-title">
          <h3 id="workflow-normal-title">Normal workflow</h3>
          <ol className="workflow-steps">
            {NORMAL_WORKFLOW.map((step) => <li key={step}>{step}</li>)}
          </ol>
        </section>

        <section aria-labelledby="workflow-terminal-title">
          <h3 id="workflow-terminal-title">When to intervene in a Worker Terminal</h3>
          <p>Use direct Terminal intervention when:</p>
          <ul>
            {DIRECT_TERMINAL_GUIDANCE.useWhen.map((item) => <li key={item}>{item}</li>)}
          </ul>
          <p>Go back through the Command Center when:</p>
          <ul>
            {DIRECT_TERMINAL_GUIDANCE.avoidWhen.map((item) => <li key={item}>{item}</li>)}
          </ul>
          <p className="workflow-guide-note">{DIRECT_TERMINAL_GUIDANCE.normalPath}</p>
        </section>
      </div>
    </aside>
  );
}
